import db from './index';
import Dexie from 'dexie';
import { computeExpandedBounds, boundsContain } from './zoneBounds';
import Zone from 'model/Zone';

/**
 * Prépare un enregistrement zone pour Dexie (bounds +500 m).
 */
export function prepareZoneRow( attrs )
{
	var row = _.extend( {}, attrs );
	var bounds = computeExpandedBounds( row.geojson );

	// Appliquer les bounds élargis si calculables
	if( bounds )
	{
		_.extend( row, bounds );
	}

	// Index multi-entry : tableau de CP
	if( !_.isArray( row.zips ) )
	{
		row.zips = row.zips ? [].concat( row.zips ) : [];
	}

	return row;
}

/**
 * Index léger en mémoire (sans Backbone) pour findByZip sync + préfiltre AABB.
 * Les geojson restent en Dexie et sont chargés à la demande pour Turf.
 */
var zoneIndex = [];

export function getZoneIndex()
{
	return zoneIndex;
}

/**
 * Recharge l'index zones depuis Dexie (métadonnées + bounds, sans geojson).
 */
export function refreshZoneIndex()
{
	return db.zones.toArray().then( function( rows )
	{
		zoneIndex = _.map( rows, function( row )
		{
			return {
				id: row.id,
				status: row.status,
				is_active: row.is_active,
				zips: row.zips || [],
				minLng: row.minLng,
				minLat: row.minLat,
				maxLng: row.maxLng,
				maxLat: row.maxLat,
			};
		} );
		return zoneIndex;
	} );
}

/**
 * Candidates AABB pour un point (sync, index mémoire).
 */
export function findZoneCandidates( lat, lng )
{
	return _.filter( zoneIndex, function( z )
	{
		return boundsContain( z, lat, lng );
	} );
}

/**
 * Recherche zone par CP (sync via index).
 */
export function findByZip( zip )
{
	zip = String( zip || '' ).replace( /\s/g, '' );
	var entry = _.find( zoneIndex, function( z )
	{
		return _.contains( z.zips, zip );
	} );
	return entry ? entry.id : null;
}

/**
 * getClosest async : préfiltre AABB puis Turf contains/distance.
 * @returns {Promise<Zone|null>} modèle Backbone éphémère
 */
export function getClosestZone( position )
{
	var lat = position.lat;
	var lng = position.lng;
	var candidates = findZoneCandidates( lat, lng );
	var ids = _.pluck( candidates, 'id' );

	// Aucun candidat AABB → scan complet Dexie (fallback rare)
	var loader = ids.length
		? db.zones.where( 'id' ).anyOf( ids ).toArray()
		: db.zones.toArray();

	return loader.then( function( rows )
	{
		var models = _.map( rows, function( row )
		{
			return new Zone( row );
		} );

		var list = {
			find: function( fn )
			{
				return _.find( models, fn );
			},
		};

		// Même priorité que ZoneList.getClosest
		return list.find( function( zone )
		{
			return zone.isActive() && zone.contains( position );
		} ) || list.find( function( zone )
		{
			return zone.isActive() && zone.distance( position ) <= 0.5;
		} ) || list.find( function( zone )
		{
			return !zone.isActive() && zone.contains( position );
		} ) || list.find( function( zone )
		{
			return !zone.isActive() && zone.distance( position ) <= 0.5;
		} ) || null;
	} );
}

/**
 * Charge une zone par id (Promise).
 */
export function getZone( id )
{
	if( id == null )
	{
		return Promise.resolve( null );
	}

	return db.zones.get( id ).then( function( row )
	{
		return row ? new Zone( row ) : null;
	} );
}

// —— CRUD générique entités ——

/**
 * Soft-delete : clé primaire uuid, fallback sur index id.
 */
function deleteByUuidOrId( table, attrs )
{
	if( attrs.uuid )
	{
		return db[table].delete( attrs.uuid );
	}

	if( attrs.id != null )
	{
		return db[table].where( 'id' ).equals( attrs.id ).delete();
	}

	console.warn( 'deleteByUuidOrId: pas de uuid ni id', table, attrs );
	return Promise.resolve();
}

export function putZone( attrs )
{
	var row = prepareZoneRow( attrs );

	// Soft-delete
	if( row.deleted_at )
	{
		if( row.id == null )
		{
			return Promise.resolve();
		}
		return db.zones.delete( row.id );
	}

	return db.zones.put( row );
}

export function putZones( list )
{
	var ops = [];

	_.each( list, function( attrs )
	{
		if( attrs.deleted_at )
		{
			if( attrs.id != null )
			{
				ops.push( db.zones.delete( attrs.id ) );
			}
		}
		else
		{
			ops.push( db.zones.put( prepareZoneRow( attrs ) ) );
		}
	} );

	return Promise.all( ops ).then( refreshZoneIndex );
}

export function putPond( attrs )
{
	if( attrs.deleted_at )
	{
		return deleteByUuidOrId( 'ponds', attrs );
	}

	return db.ponds.put( _.extend( {}, attrs ) );
}

export function putPonds( list )
{
	return db.transaction( 'rw', db.ponds, function()
	{
		_.each( list, function( attrs )
		{
			if( attrs.deleted_at )
			{
				deleteByUuidOrId( 'ponds', attrs );
			}
			else
			{
				db.ponds.put( _.extend( {}, attrs ) );
			}
		} );
	} );
}

export function putVisit( attrs )
{
	if( attrs.deleted_at )
	{
		return deleteByUuidOrId( 'visits', attrs );
	}

	return db.visits.put( _.extend( {}, attrs ) );
}

export function putVisits( list )
{
	return db.transaction( 'rw', db.visits, function()
	{
		_.each( list, function( attrs )
		{
			if( attrs.deleted_at )
			{
				deleteByUuidOrId( 'visits', attrs );
			}
			else
			{
				db.visits.put( _.extend( {}, attrs ) );
			}
		} );
	} );
}

export function putImage( attrs )
{
	if( attrs.deleted_at )
	{
		return deleteByUuidOrId( 'images', attrs );
	}

	return db.images.put( _.extend( {}, attrs ) );
}

export function putImages( list )
{
	return db.transaction( 'rw', db.images, function()
	{
		_.each( list, function( attrs )
		{
			if( attrs.deleted_at )
			{
				deleteByUuidOrId( 'images', attrs );
			}
			else
			{
				db.images.put( _.extend( {}, attrs ) );
			}
		} );
	} );
}

export function getPond( uuid )
{
	return db.ponds.get( uuid );
}

export function getVisit( uuid )
{
	return db.visits.get( uuid );
}

export function getImage( uuid )
{
	return db.images.get( uuid );
}

/**
 * Lecture générique par uuid (ponds / visits / images).
 */
export function getByUuid( table, uuid )
{
	if( !db[table] || !uuid )
	{
		return Promise.resolve( undefined );
	}

	return db[table].get( uuid );
}

/**
 * Applique une ligne distante de façon idempotente.
 * Retourne true si Dexie a changé.
 * Skip si brouillon local (todo), même etag, ou updated_at local plus récent.
 */
export function applyRemoteRow( table, attrs )
{
	if( !db[table] || !attrs || !attrs.uuid )
	{
		return Promise.resolve( false );
	}

	var writers = {
		ponds: putPond,
		visits: putVisit,
		images: putImage,
	};
	var write = writers[table];
	if( !write )
	{
		return Promise.resolve( false );
	}

	return db[table].get( attrs.uuid ).then( function( local )
	{
		if( local && local.todo )
		{
			return false;
		}

		if( local && attrs.etag && local.etag && local.etag === attrs.etag )
		{
			return false;
		}

		var localTs = local && local.updated_at != null ? Number( local.updated_at ) : null;
		var remoteTs = attrs.updated_at != null ? Number( attrs.updated_at ) : null;
		if( localTs != null && remoteTs != null && !isNaN( localTs ) && !isNaN( remoteTs ) && localTs > remoteTs )
		{
			return false;
		}

		return write( attrs ).then( function()
		{
			return true;
		} );
	} );
}

/**
 * Mares dans les bounds carte (viewport).
 */
export function getPondsInBounds( bounds )
{
	var west = bounds.getWest();
	var east = bounds.getEast();
	var south = bounds.getSouth();
	var north = bounds.getNorth();

	return db.ponds
		.filter( function( p )
		{
			if( p.todo === 'delete' )
			{
				return false;
			}

			if( !_.isNumber( p.lat ) || !_.isNumber( p.lng ) )
			{
				return false;
			}

			return p.lng >= west && p.lng <= east && p.lat >= south && p.lat <= north;
		} )
		.toArray();
}

export function getVisitsByPond( pondUuid )
{
	return db.visits.where( 'pond_uuid' ).equals( pondUuid ).toArray();
}

export function getImagesByPond( pondUuid )
{
	return db.images.where( 'pond_uuid' ).equals( pondUuid ).toArray();
}

export function getPending( table )
{
	return db[table]
		.filter( function( row )
		{
			return _.contains( [ 'create', 'update', 'delete' ], row.todo );
		} )
		.toArray();
}

/**
 * Compteurs pour l'écran d'accueil.
 * Totaux globaux depuis /data.json ; mine / drafts depuis Dexie.
 */
export function getHomeCounts( userUuid, userId )
{
	return Promise.all( [
		db.zones.count(),
		db.ponds.toArray(),
		db.visits.toArray(),
	] ).then( function( results )
	{
		var zoneNb = results[0];
		var ponds = results[1];
		var visits = results[2];
		var data = app.getStore( 'data' );

		var visibleVisits = _.filter( visits, function( v )
		{
			return v.todo !== 'delete';
		} );

		var draftNb = _.size( _.filter( visibleVisits, function( v )
		{
			return v.user_id == userId && v.todo;
		} ) );

		var sentNb = _.size( _.filter( visibleVisits, function( v )
		{
			return v.user_id == userId && !v.todo;
		} ) );

		// Compteurs « Mes mares / Mes visites » (user_uuid)
		var minePondNb = userUuid
			? _.size( _.filter( ponds, function( p )
			{
				return p.todo !== 'delete' && p.user_uuid == userUuid;
			} ) )
			: 0;

		var mineVisitNb = userUuid
			? _.size( _.filter( visibleVisits, function( v )
			{
				return v.user_uuid == userUuid;
			} ) )
			: 0;

		return {
			zone_nb: zoneNb,
			pond_nb: data ? ( data.get( 'pond_nb' ) || 0 ) : 0,
			visit_nb: data ? ( data.get( 'visit_nb' ) || 0 ) : 0,
			draft_nb: draftNb,
			sent_nb: sentNb,
			mine_pond_nb: minePondNb,
			mine_visit_nb: mineVisitNb,
		};
	} );
}

/**
 * Visites filtrées draft/sent pour un utilisateur.
 */
export function getVisitsByType( type, userId )
{
	return db.visits.toArray().then( function( rows )
	{
		return _.filter( rows, function( visit )
		{
			if( visit.todo === 'delete' )
			{
				return false;
			}

			if( visit.user_id != userId )
			{
				return false;
			}

			if( type === 'draft' )
			{
				return !!visit.todo;
			}

			if( type === 'sent' )
			{
				return !visit.todo;
			}

			return false;
		} );
	} );
}

/**
 * Collection Dexie triée DESC via index [user_uuid+created_at], hors soft-delete.
 */
function collectionByUser( table, userUuid )
{
	return db[table]
		.where( '[user_uuid+created_at]' )
		.between( [ userUuid, Dexie.minKey ], [ userUuid, Dexie.maxKey ] )
		.reverse()
		.filter( function( row )
		{
			return row.todo !== 'delete';
		} );
}

/**
 * Liste paginée des mares d'un utilisateur (created_at DESC).
 */
export function getPondsByUser( userUuid, options )
{
	options = options || {};
	var offset = options.offset || 0;
	var limit = options.limit || 20;

	// Sans uuid : liste vide
	if( !userUuid )
	{
		return Promise.resolve( { items: [], total: 0 } );
	}

	var coll = collectionByUser( 'ponds', userUuid );

	return Promise.all( [
		collectionByUser( 'ponds', userUuid ).count(),
		coll.offset( offset ).limit( limit ).toArray(),
	] ).then( function( results )
	{
		return { items: results[1], total: results[0] };
	} );
}

/**
 * Liste paginée des visites d'un utilisateur (created_at DESC).
 */
export function getVisitsByUser( userUuid, options )
{
	options = options || {};
	var offset = options.offset || 0;
	var limit = options.limit || 20;

	// Sans uuid : liste vide
	if( !userUuid )
	{
		return Promise.resolve( { items: [], total: 0 } );
	}

	var coll = collectionByUser( 'visits', userUuid );

	return Promise.all( [
		collectionByUser( 'visits', userUuid ).count(),
		coll.offset( offset ).limit( limit ).toArray(),
	] ).then( function( results )
	{
		return { items: results[1], total: results[0] };
	} );
}

export function saveSingleton( table, attrs )
{
	var row = _.extend( { key: 'current' }, attrs );
	return db[table].put( row );
}

export function loadSingleton( table )
{
	return db[table].get( 'current' );
}

export function setMeta( key, value )
{
	return db.meta.put( { key: key, value: value } );
}

export function getMeta( key )
{
	return db.meta.get( key ).then( function( row )
	{
		return row ? row.value : undefined;
	} );
}

/**
 * Clé meta du curseur since (global ou par userUuid).
 */
export function sinceKey( table, userUuid )
{
	return userUuid ? ( 'since_' + table + '_' + userUuid ) : ( 'since_' + table );
}

/**
 * Curseur de synchro incrémentale (ms) stocké dans meta Dexie.
 */
export function getSince( table, userUuid )
{
	return getMeta( sinceKey( table, userUuid ) ).then( function( value )
	{
		return _.isNumber( value ) ? value : null;
	} );
}

/**
 * Met à jour le curseur since si le nouveau timestamp est plus récent.
 */
export function bumpSince( table, timestamps, userUuid )
{
	var max = _.max( _.compact( timestamps ) );
	if( !_.isNumber( max ) || !isFinite( max ) )
	{
		return Promise.resolve();
	}

	return getSince( table, userUuid ).then( function( current )
	{
		if( current != null && max <= current )
		{
			return;
		}

		return setMeta( sinceKey( table, userUuid ), max );
	} );
}

/**
 * Supprime les curseurs privés d'un utilisateur.
 */
export function clearUserSince( userUuid )
{
	if( !userUuid )
	{
		return Promise.resolve();
	}

	return Promise.all( [
		db.meta.delete( sinceKey( 'ponds', userUuid ) ),
		db.meta.delete( sinceKey( 'visits', userUuid ) ),
		db.meta.delete( sinceKey( 'images', userUuid ) ),
	] );
}

/**
 * Curseurs since globaux (ancien sync bulk published=1).
 */
export function clearGlobalSince()
{
	return Promise.all( [
		db.meta.delete( sinceKey( 'ponds' ) ),
		db.meta.delete( sinceKey( 'visits' ) ),
		db.meta.delete( sinceKey( 'images' ) ),
	] );
}

/**
 * Purge one-shot de l'ancien dump public bulk.
 * Conserve mine (userUuid) + brouillons locaux (todo).
 */
export function purgeBulkPublicDump( userUuid )
{
	return db.transaction( 'rw', db.ponds, db.visits, db.images, function()
	{
		return Promise.all( [
			db.ponds.toArray(),
			db.visits.toArray(),
			db.images.toArray(),
		] ).then( function( results )
		{
			var ponds = results[0];
			var visits = results[1];
			var images = results[2];

			var keepPond = function( row )
			{
				if( row.todo )
				{
					return true;
				}
				if( userUuid && row.user_uuid === userUuid )
				{
					return true;
				}
				return false;
			};

			var keepAuthor = function( row )
			{
				if( row.todo )
				{
					return true;
				}
				if( userUuid && row.user_uuid === userUuid )
				{
					return true;
				}
				return false;
			};

			var dropPonds = _.pluck( _.reject( ponds, keepPond ), 'uuid' );
			var dropVisits = _.pluck( _.reject( visits, keepAuthor ), 'uuid' );
			var dropImages = _.pluck( _.reject( images, keepAuthor ), 'uuid' );

			return Promise.all( [
				dropPonds.length ? db.ponds.bulkDelete( dropPonds ) : Promise.resolve(),
				dropVisits.length ? db.visits.bulkDelete( dropVisits ) : Promise.resolve(),
				dropImages.length ? db.images.bulkDelete( dropImages ) : Promise.resolve(),
			] );
		} );
	} );
}

/**
 * Passe en mode sync mine + cache opportuniste (une seule fois).
 */
export function ensureMineSyncMode()
{
	return getMeta( 'sync_mode' ).then( function( mode )
	{
		if( mode === 'mine' )
		{
			return;
		}

		var userUuid = null;
		try
		{
			userUuid = app.getStore( 'user' ).get( 'uuid' ) || null;
		}
		catch( e )
		{
			userUuid = null;
		}

		return purgeBulkPublicDump( userUuid ).then( function()
		{
			return clearGlobalSince();
		} ).then( function()
		{
			return setMeta( 'sync_mode', 'mine' );
		} );
	} );
}

/**
 * Purge locale des données non publiques + brouillons du user.
 */
export function purgeUnpublished( userUuid )
{
	return db.transaction( 'rw', db.ponds, db.visits, db.images, function()
	{
		return db.ponds.toArray().then( function( allPonds )
		{
			var privateUuids = _.pluck( _.filter( allPonds, function( p )
			{
				return p.is_published === false || p.is_published === 0;
			} ), 'uuid' );

			var keepUuids = _.pluck( _.filter( allPonds, function( p )
			{
				if( _.contains( privateUuids, p.uuid ) )
				{
					return false;
				}
				// Brouillon local du user : à purger aussi
				if( userUuid && p.user_uuid === userUuid && p.todo )
				{
					return false;
				}
				return true;
			} ), 'uuid' );

			var dropPondUuids = _.difference( _.pluck( allPonds, 'uuid' ), keepUuids );

			return Promise.all( [
				dropPondUuids.length ? db.ponds.bulkDelete( dropPondUuids ) : Promise.resolve(),
				db.visits.filter( function( v )
				{
					if( _.contains( dropPondUuids, v.pond_uuid ) )
					{
						return true;
					}
					if( !_.contains( keepUuids, v.pond_uuid ) )
					{
						return true;
					}
					if( userUuid && v.user_uuid === userUuid && v.todo )
					{
						return true;
					}
					return false;
				} ).delete(),
				db.images.filter( function( i )
				{
					if( _.contains( dropPondUuids, i.pond_uuid ) )
					{
						return true;
					}
					if( i.pond_uuid && !_.contains( keepUuids, i.pond_uuid ) )
					{
						return true;
					}
					if( userUuid && i.user_uuid === userUuid && i.todo )
					{
						return true;
					}
					return false;
				} ).delete(),
			] );
		} );
	} );
}
